import React, { useState, useEffect } from 'react';
import { NavLink } from 'react-router-dom';
import AdminLayout from '../../components/layouts/AdminLayout';
import { Card, CardHeader, CardTitle, CardContent } from '../../components/ui/Card';
import Button from '../../components/ui/Button';
import Icon from '../../components/AppIcon';
import Input from '../../components/ui/Input';
import Select from '../../components/ui/Select';
import { hierarchyService } from '../../services/hierarchyService';
import { userService } from '../../services/userService';
import HierarchyEntityTable from './HierarchyEntityTable';
import ActionToast from '../../components/ui/ActionToast';

const Section = () => {
    const [sections, setSections] = useState([]);
    const [departments, setDepartments] = useState([]);
    const [hoss, setHoss] = useState([]);

    // Filter State
    const [selectedDeptId, setSelectedDeptId] = useState('all');

    const [loading, setLoading] = useState(true);
    const [showForm, setShowForm] = useState(false);
    const [isEditing, setIsEditing] = useState(false);
    const [editId, setEditId] = useState(null);
    const [error, setError] = useState(null);
    const [notice, setNotice] = useState(null);

    // Form data
    const [formData, setFormData] = useState({
        name: '',
        code: '',
        department_id: '',
        hos_id: '',
        description: ''
    });

    // Loading states for dropdowns
    const [loadingDepartments, setLoadingDepartments] = useState(false);
    const [loadingHoss, setLoadingHoss] = useState(false);

    const fetchData = async () => {
        try {
            setLoading(true);
            setError(null);
            const data = await hierarchyService.getSections();
            setSections(data || []);
        } catch (error) {
            console.error('Failed to fetch sections:', error);
            setError(`Failed to load sections: ${error.message || 'Unknown error'}`);
        } finally {
            setLoading(false);
        }
    };

    const fetchDropdowns = async () => {
        // Fetch Departments
        try {
            setLoadingDepartments(true);
            const deptData = await hierarchyService.getDepartments();
            if (deptData) {
                setDepartments(deptData.map(d => ({ label: d.name, value: d.id, original: d })));
            } else {
                console.warn("No departments returned from service");
            }
        } catch (e) {
            console.error("Error fetching departments:", e);
        } finally {
            setLoadingDepartments(false);
        }

        // Fetch HOSs
        try {
            setLoadingHoss(true);
            const userData = await userService.getUsers({ role: 'head_of_section' });
            if (userData) {
                setHoss(userData.map(u => ({ label: `${u.full_name} (${u.email})`, value: u.id })));
            }
        } catch (e) { console.error("Error fetching HOSs:", e); } finally { setLoadingHoss(false); }
    };

    useEffect(() => {
        fetchData();
        fetchDropdowns();
    }, []);

    const clearForm = () => {
        setFormData({ name: '', code: '', department_id: '', hos_id: '', description: '' });
        setIsEditing(false);
        setEditId(null);
    };

    const handleEdit = (section) => {
        setFormData({
            name: section.name,
            code: section.code,
            department_id: section.department_id || '',
            hos_id: section.hos_id || '',
            description: section.description || ''
        });
        setEditId(section.id);
        setIsEditing(true);
        setShowForm(true);
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    const handleDelete = async (id) => {
        if (window.confirm('Are you sure you want to delete this section? This action cannot be undone.')) {
            try {
                await hierarchyService.deleteSection(id);
                fetchData();
            } catch (error) {
                console.error('Error deleting section:', error);
                alert('Failed to delete section.');
            }
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            if (isEditing) {
                await hierarchyService.updateSection(editId, formData);
                setNotice({ type: 'success', message: `${formData.name} was updated successfully.` });
            } else {
                await hierarchyService.createSection(formData);
                setNotice({ type: 'success', message: `${formData.name} was created successfully.` });
            }
            clearForm();
            setShowForm(false);
            fetchData();
        } catch (error) {
            console.error('Error saving section:', error);
            setNotice({ type: 'error', message: error.message || 'The section could not be saved.' });
        }
    };

    const handleToggleForm = (e) => {
        e?.preventDefault(); // Prevent accidental form submission triggers if inside a form (unlikely here)
        console.log("Toggling form. Current state:", showForm);
        if (showForm && !isEditing) {
            clearForm();
            setShowForm(false);
        } else {
            clearForm();
            setShowForm(true);
        }
    };

    // Filter Logic
    const filteredSections = selectedDeptId === 'all'
        ? sections
        : sections.filter(s => s.department_id === selectedDeptId);

    const departmentOptions = departments;

    return (
        <AdminLayout title="Section Management">
            <ActionToast notice={notice} onClose={() => setNotice(null)} />
            <div className="space-y-6">
                <div className="flex justify-between items-center sm:flex-row flex-col gap-4 sm:gap-0">
                    <div>
                        <h1 className="text-3xl font-bold tracking-tight">Sections</h1>
                        <p className="text-muted-foreground">Manage sections grouped by Department.</p>
                    </div>
                    {/* Explicitly ensuring button is clickable and not covered */}
                    <div className="z-10 relative">
                        <Button onClick={handleToggleForm}>
                            <Icon name={showForm && !isEditing ? "X" : "Plus"} className="mr-2 h-4 w-4" />
                            {showForm && !isEditing ? 'Cancel' : 'Add Section'}
                        </Button>
                    </div>
                </div>

                {error && (
                    <div className="bg-destructive/15 text-destructive p-4 rounded-md">
                        {error}
                    </div>
                )}

                {/* Department Filter Bar */}
                <div className="w-full overflow-x-auto pb-2 border-b">
                    <div className="flex gap-2 min-w-max">
                        <Button
                            variant={selectedDeptId === 'all' ? "default" : "outline"}
                            size="sm"
                            onClick={() => setSelectedDeptId('all')}
                            className="rounded-full"
                        >
                            All Sections
                        </Button>
                        {departmentOptions.length > 0 ? (
                            departmentOptions.map(dept => (
                                <Button
                                    key={dept.value}
                                    variant={selectedDeptId === dept.value ? "default" : "outline"}
                                    size="sm"
                                    onClick={() => setSelectedDeptId(dept.value)}
                                    className="rounded-full"
                                >
                                    {dept.label}
                                </Button>
                            ))
                        ) : (
                            // Helper if no departments found
                            <span className="text-sm text-muted-foreground flex items-center px-2">
                                No departments found.
                                <NavLink to="/hierarchy/department" className="ml-1 underline text-primary">Create one?</NavLink>
                            </span>
                        )}
                    </div>
                </div>

                {showForm && (
                    <Card className="border-primary/20 bg-primary/5 animate-in fade-in slide-in-from-top-4 duration-300">
                        <CardHeader>
                            <CardTitle>{isEditing ? 'Edit Section' : 'Create New Section'}</CardTitle>
                        </CardHeader>
                        <CardContent>
                            <form onSubmit={handleSubmit} className="space-y-4">
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    <Input
                                        label="Section Name"
                                        value={formData.name}
                                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                                        required
                                        placeholder="e.g. Software Engineering"
                                    />
                                    <Input
                                        label="Section Code"
                                        value={formData.code}
                                        onChange={(e) => setFormData({ ...formData, code: e.target.value })}
                                        required
                                        placeholder="e.g. SE"
                                    />

                                    {/* Department Select with explicit empty state message */}
                                    <div className="space-y-1">
                                        <Select
                                            label="Department"
                                            options={departments}
                                            value={formData.department_id}
                                            onChange={(val) => setFormData({ ...formData, department_id: val })}
                                            loading={loadingDepartments}
                                            placeholder={loadingDepartments ? "Loading departments..." : "Select Department..."}
                                            searchable
                                            required
                                        />
                                        {departments.length === 0 && !loadingDepartments && (
                                            <p className="text-xs text-destructive">
                                                No departments available. Please create a department first.
                                            </p>
                                        )}
                                    </div>

                                    <div className="space-y-1">
                                        <Select
                                            label="Head of Section"
                                            options={hoss}
                                            value={formData.hos_id}
                                            onChange={(val) => setFormData({ ...formData, hos_id: val })}
                                            loading={loadingHoss}
                                            placeholder="Select HOS..."
                                            searchable
                                            required
                                        />
                                        <div className="text-xs text-muted-foreground text-right">
                                            Can't find the person?{' '}
                                            <NavLink to="/enhanced-user-management-system" className="text-primary hover:underline">
                                                Assign 'HOS' Role
                                            </NavLink>
                                        </div>
                                    </div>
                                    <Input
                                        label="Description"
                                        value={formData.description}
                                        onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                                        placeholder="Brief description..."
                                        className="md:col-span-2"
                                    />
                                </div>
                                <div className="flex justify-end gap-2 mt-4">
                                    <Button type="button" variant="outline" onClick={() => { clearForm(); setShowForm(false); }}>Cancel</Button>
                                    <Button type="submit">{isEditing ? 'Update Section' : 'Create Section'}</Button>
                                </div>
                            </form>
                        </CardContent>
                    </Card>
                )}

                {loading ? (
                    <div className="text-center py-10">Loading sections...</div>
                ) : (
                    <HierarchyEntityTable type="section" entities={filteredSections} onEdit={handleEdit} onDelete={handleDelete} emptyMessage={selectedDeptId === 'all' ? 'No sections found. Click "Add Section" to create one.' : 'No sections found in this department.'} />
                )}
            </div>
        </AdminLayout>
    );
};

export default Section;
